/** The library: every report the person has, newest first, grouped Today /
 *  This week / Earlier, an unread dot on the ones waiting to be read, beside
 *  the open report - under the report templates on the one Reports page
 *  (`above`, item 78). Filters: words in the title, one report template,
 *  dates, unread only; they and the page's actions are its one sticky bar.
 *  While a report is being written the list polls (and the server's poll
 *  keeps the writer going - report_runner.wake). */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker, Input, Select, message as antdMessage } from 'antd';
import { ConfirmModal } from '@/components/common/ConfirmModal';
import { ExclamationCircleOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import type { Dayjs } from 'dayjs';
import { useLanguage } from '@/hooks/useLanguage';
import insightsService from '../../services/insightsService';
import type { LibraryReport, ReportDetail, ReportTemplate } from '../../types/insights';
import { InsightsStickyBar, type FilterSlots } from '../InsightsStickyBar';
import { ReportReader } from './ReportReader';
import { groupByRecency, reportError, whenLabel, windowLabel } from './reportParts';

const PAGE = 30;
/** Where the list and the report stack (Reports.css): a pick scrolls to the report. */
const STACKED = '(max-width: 900px)';
const POLL_MS = 5000;
const SEARCH_DELAY_MS = 300;

interface Props {
  templates: ReportTemplate[];
  selectedId: string | null;
  onSelect: (reportId: string | null) => void;
  onOpenTemplate: (templateId: string) => void;
  /** Write a report again (Try again on one that failed). */
  onRetry: (report: LibraryReport) => void;
  /** Bumped by the parent after a Run now, so the new row shows at once. */
  refreshKey: number;
  /** What the page shows above the library (the report templates). */
  above: ReactNode;
  /** The page's actions: they stay at the top with the filters (InsightsStickyBar). */
  actions: ReactNode;
}

export function ReportLibrary({ templates, selectedId, onSelect, onOpenTemplate, onRetry, refreshKey, above,
  actions }: Props) {
  const { t } = useLanguage();
  // Reports waiting to be read: the Library heading's badge.
  const [unread, setUnread] = useState(0);
  const [reports, setReports] = useState<LibraryReport[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [inFlight, setInFlight] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [templateId, setTemplateId] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [open, setOpen] = useState<ReportDetail | null>(null);
  const [opening, setOpening] = useState(false);
  const openedRef = useRef<string | null>(null);
  const selectedRef = useRef(selectedId);
  selectedRef.current = selectedId;

  useEffect(() => {
    const id = window.setTimeout(() => setSearch(query.trim()), SEARCH_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [query]);

  const filters = useMemo(() => ({
    templateId, unread: unreadOnly, q: search,
    since: range?.[0]?.format('YYYY-MM-DD'), until: range?.[1]?.format('YYYY-MM-DD'),
  }), [templateId, unreadOnly, search, range]);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const page = await insightsService.listReports({ ...filters, limit: PAGE, offset: 0 });
      // The open report stays in the list while the filters are unchanged,
      // though reading it took it out of "Unread only".
      setReports((prev) => {
        const open = prev.find((r) => r.report_id === selectedRef.current);
        if (!open || page.reports.some((r) => r.report_id === open.report_id)) return page.reports;
        return [...page.reports, open].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
      });
      setHasMore(page.has_more);
      setInFlight(page.in_flight);
      setUnread(page.unread_count);
      setError(null);
    } catch (err) {
      if (!quiet) setError(reportError(t, err));
    } finally {
      if (!quiet) setLoading(false);
    }
    // `t` and the callback are stable enough; a language change re-renders the words, not the data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => { load(); }, [load, refreshKey]);
  // A report just started (refreshKey) is opened at once: no filter may hide it from the list.
  useEffect(() => {
    if (!refreshKey) return;
    setQuery('');
    setSearch('');
    setTemplateId(undefined);
    setRange(null);
    setUnreadOnly(false);
  }, [refreshKey]);

  // While a report is being written, watch it (quietly).
  useEffect(() => {
    if (!inFlight) return;
    const id = window.setInterval(() => load(true), POLL_MS);
    return () => window.clearInterval(id);
  }, [inFlight, load]);

  const loadMore = async () => {
    const page = await insightsService.listReports({ ...filters, limit: PAGE, offset: reports.length });
    setReports((prev) => [...prev, ...page.reports.filter((r) => !prev.some((p) => p.report_id === r.report_id))]);
    setHasMore(page.has_more);
  };

  const selected = reports.find((r) => r.report_id === selectedId) || null;

  /** Open a ready report - which reads it (and its bell item). */
  const openOne = useCallback((report: LibraryReport) => {
    openedRef.current = report.report_id;
    setOpening(true);
    insightsService.openReport(report.report_id)
      .then((detail) => {
        setOpen(detail);
        if (!report.read) {
          setReports((prev) => prev.map((r) => (r.report_id === detail.report_id ? { ...r, read: true } : r)));
          setUnread((n) => Math.max(0, n - 1));
        }
      })
      .catch(() => setOpen(null))
      .finally(() => setOpening(false));
  }, []);

  // Open the selected report once it is ready.
  useEffect(() => {
    if (!selected || selected.status !== 'ready') {
      setOpen(null);
      openedRef.current = null;
      return;
    }
    if (openedRef.current === selected.report_id) return;
    openOne(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.report_id, selected?.status]);

  const pick = (report: LibraryReport) => {
    if (report.report_id === selectedId) {
      if (report.status === 'ready' && !report.read) openOne(report);   // picked again after "Mark unread"
      return;
    }
    onSelect(report.report_id);
    if (window.matchMedia(STACKED).matches) {
      window.requestAnimationFrame(() => document.getElementById('rp-reader')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  };

  // The report stays open and stays unread: it is opened (read) again only when
  // the person picks it again.
  const markUnread = async (reportId: string) => {
    await insightsService.markReportUnread(reportId);
    setReports((prev) => prev.map((r) => (r.report_id === reportId ? { ...r, read: false } : r)));
    setUnread((n) => n + 1);
  };

  // Delete one report (asked first, ConfirmModal below); the next one in the list is not opened by itself.
  const [deleting, setDeleting] = useState<LibraryReport | null>(null);
  const remove = async (report: LibraryReport) => {
    setDeleting(null);
    try {
      await insightsService.deleteReport(report.report_id);
      setReports((prev) => prev.filter((r) => r.report_id !== report.report_id));
      const row = reports.find((r) => r.report_id === report.report_id) || report;
      if (row.status === 'ready' && !row.read) setUnread((n) => Math.max(0, n - 1));
      onSelect(null);
      antdMessage.success(t('insights.reports.reader.deleted'));
    } catch (err) {
      antdMessage.error(reportError(t, err));
    }
  };

  const templateName = (id: string | null) => templates.find((tpl) => tpl.template_id === id)?.name;
  const groups = groupByRecency(reports);
  const filterCount = [search, templateId, range, unreadOnly].filter(Boolean).length;

  // On a wider screen the page's buttons (Run a report now, New report template) end this row (item 93).
  const filterRow = ({ start, end }: FilterSlots) => (
      <div className="rp-filters" role="search">
        {start}
        <Input
          className="rp-filters__search"
          allowClear
          prefix={<SearchOutlined aria-hidden="true" />}
          placeholder={t('insights.reports.filters.search')}
          aria-label={t('insights.reports.filters.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Select
          allowClear
          value={templateId}
          onChange={setTemplateId}
          placeholder={t('insights.reports.filters.allTemplates')}
          aria-label={t('insights.reports.filters.template')}
          options={templates.map((tpl) => ({ value: tpl.template_id, label: tpl.name }))}
          popupMatchSelectWidth={false}
        />
        <DatePicker.RangePicker
          value={range}
          onChange={(value) => setRange(value as [Dayjs | null, Dayjs | null] | null)}
          allowEmpty={[true, true]}
          aria-label={t('insights.reports.filters.dates')}
        />
        <button type="button" className={`cx-btn ${unreadOnly ? 'is-on' : ''}`} aria-pressed={unreadOnly}
                onClick={() => setUnreadOnly((v) => !v)} data-testid="reports-unread-only">
          {t('insights.reports.filters.unreadOnly')}
        </button>
        {end && <span className="ins-scope__end">{end}</span>}
      </div>
  );

  return (
    <div className="rp">
      <InsightsStickyBar actions={actions} filters={filterRow} filterCount={filterCount} testId="reports-sticky" />

      {above}

      <section className="rp-area" aria-labelledby="rp-library-title">
      <h2 className="rp-area__title" id="rp-library-title">
        {t('insights.reports.library.tab')}
        {unread > 0 && <span className="rp-badge" aria-label={t('insights.reports.library.unreadCount', { n: unread })}>
          {unread > 99 ? '99+' : unread}</span>}
      </h2>
      <div className="rp-room" data-testid="reports-library">
        <nav className="rp-inbox" aria-label={t('insights.reports.library.label')} aria-busy={loading}>
          {loading ? (
            <ul className="rp-inbox__list" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <li key={i} className="rp-row rp-row--skeleton"><span /><span><span className="tpl-skel rp-skel--line" />
                  <span className="tpl-skel rp-skel--short" /></span><span /></li>
              ))}
            </ul>
          ) : error ? (
            <div className="rp-empty" role="alert">
              <strong>{error}</strong>
              <button type="button" className="cx-btn" onClick={() => load()}>{t('common.retry')}</button>
            </div>
          ) : reports.length === 0 ? (
            <div className="rp-empty">
              <strong>{t(filterCount ? 'insights.reports.library.noMatchTitle' : 'insights.reports.library.emptyTitle')}</strong>
              <span>{t(filterCount ? 'insights.reports.library.noMatchBody' : 'insights.reports.library.emptyBody')}</span>
            </div>
          ) : (
            <>
              {groups.map((group) => (
                <section key={group.key} aria-labelledby={`rp-group-${group.key}`}>
                  <h3 id={`rp-group-${group.key}`} className="rp-inbox__group">
                    {t(`insights.reports.library.${group.key}`)}
                  </h3>
                  <ul className="rp-inbox__list">
                    {group.reports.map((r) => {
                      const busy = r.status === 'queued' || r.status === 'running';
                      const problem = r.status === 'failed' || r.status === 'skipped';
                      const unread = r.status === 'ready' && !r.read;
                      const source = r.trigger === 'manual' ? t('insights.reports.library.byHand')
                        : r.trigger === 'migrated' ? t('insights.reports.library.earlierReport')
                          : templateName(r.template_id) ? t('insights.reports.library.scheduled') : '';
                      return (
                        <li key={r.report_id}>
                          <button type="button"
                                  className={`rp-row ${r.report_id === selectedId ? 'is-selected' : ''}`}
                                  aria-current={r.report_id === selectedId ? 'true' : undefined}
                                  onClick={() => pick(r)}>
                            <span className={`rp-dot ${unread ? '' : 'is-read'}`}
                                  aria-label={unread ? t('insights.reports.library.unread') : undefined} />
                            <span>
                              <span className={`rp-row__title ${unread ? '' : 'is-read'}`}><bdi dir="auto">{r.title}</bdi></span>
                              <span className="rp-row__meta">
                                {[windowLabel(r.period, { year: false }), source].filter(Boolean).join(' · ')}
                              </span>
                              {busy && (
                                <span className="rp-row__state">
                                  <LoadingOutlined spin aria-hidden="true" /> {t('insights.reports.library.writing')}
                                </span>
                              )}
                              {problem && (
                                <span className="rp-row__state is-problem">
                                  <ExclamationCircleOutlined aria-hidden="true" />
                                  {t(r.status === 'failed' ? 'insights.reports.library.failed'
                                    : 'insights.reports.library.skipped')}
                                </span>
                              )}
                            </span>
                            <span className="rp-row__when">{whenLabel(r.finished_at || r.created_at)}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
              {hasMore && (
                <div className="rp-more">
                  <button type="button" className="cx-btn" onClick={loadMore}>{t('insights.reports.library.more')}</button>
                </div>
              )}
            </>
          )}
        </nav>

        <ReportReader summary={selected} report={open} loading={opening}
                      onMarkUnread={markUnread} onOpenTemplate={onOpenTemplate} onDelete={setDeleting} onRetry={onRetry} />
      </div>
      </section>

      <ConfirmModal
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => { if (deleting) void remove(deleting); }}
        title={deleting ? t('insights.reports.reader.deleteTitle', { name: deleting.title }) : ''}
        message={t('insights.reports.reader.deleteBody')}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        variant="danger"
      />
    </div>
  );
}
